import { TZDate } from '@date-fns/tz';
import { differenceInHours } from 'date-fns';

import { formatUpdatedAt } from '@air/utilities';

import { type AutomationDetailFields } from '~/components/Automations/detail/automationDetailFields';
import {
  formatAutomationDayOrdinalSuffix,
  formatAutomationRunTimestamp,
  formatAutomationScheduleClock,
  formatAutomationWeekdaysLabel,
  getAutomationCronDisplayTimeZone,
  getAutomationNextRunZoned,
  parseAutomationSchedule,
  timeZoneAbbreviation,
} from '~/components/Automations/utils/automationSchedule';

const LIST_LAST_RUN_RELATIVE_HOURS = 48;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export type AutomationListStatus = 'active' | 'failed' | 'paused';

export type AutomationListTriggerLines = {
  primary: string;
  secondary: string | null;
};

type ZonedParts = {
  day: number;
  hour: number;
  minute: number;
  month: number;
  weekday: number;
  year: number;
};

const zonedPartsToIso = (parts: ZonedParts, timeZone: string) =>
  new TZDate(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0, timeZone).toISOString();

const appendTimeZoneAbbrev = (label: string, cronSchedule: string, now: Date, viewerTimeZone: string) => {
  const displayTimeZone = getAutomationCronDisplayTimeZone(cronSchedule, viewerTimeZone);
  const zone = timeZoneAbbreviation(displayTimeZone, now);

  return `${label} ${zone}`;
};

export const resolveAutomationListStatus = (automation: AutomationDetailFields): AutomationListStatus => {
  if (!automation.enabled) {
    return 'paused';
  }

  if (automation.lastRunStatus === 'failed') {
    return 'failed';
  }

  return 'active';
};

export const automationListStatusSortRank = (status: AutomationListStatus) => {
  if (status === 'failed') {
    return 0;
  }
  if (status === 'active') {
    return 1;
  }

  return 2;
};

export const formatAutomationListTrigger = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}): AutomationListTriggerLines => {
  const schedule = parseAutomationSchedule({ cronSchedule, now, timeZone });

  if (!schedule) {
    return { primary: cronSchedule.trim(), secondary: null };
  }

  const displayTimeZone = getAutomationCronDisplayTimeZone(cronSchedule, timeZone);
  const zone = timeZoneAbbreviation(displayTimeZone, now);

  if (schedule.repeat === 'minute') {
    return { primary: 'Every minute', secondary: null };
  }
  if (schedule.repeat === 'hour') {
    return { primary: 'Every hour', secondary: null };
  }
  if (schedule.repeat === '5min') {
    return { primary: 'Every 5 minutes', secondary: null };
  }
  if (schedule.repeat === '15min') {
    return { primary: 'Every 15 minutes', secondary: null };
  }
  if (schedule.repeat === '30min') {
    return { primary: 'Every 30 minutes', secondary: null };
  }

  const clock = formatAutomationScheduleClock(schedule.hour, schedule.minute);
  const secondary = `${clock} ${zone}`;

  if (schedule.repeat === 'day') {
    return { primary: 'Every day', secondary };
  }
  if (schedule.repeat === 'weekdays') {
    return { primary: 'Every weekday', secondary };
  }
  if (schedule.repeat === 'week') {
    return { primary: `Every ${formatAutomationWeekdaysLabel(schedule.weekdays)}`, secondary };
  }

  return {
    primary: `Every month on the ${schedule.dayOfMonth}${formatAutomationDayOrdinalSuffix(schedule.dayOfMonth)}`,
    secondary,
  };
};

export const formatAutomationListLastRun = ({
  cronSchedule,
  lastRunAt,
  now,
  timeZone,
}: {
  cronSchedule: string;
  lastRunAt: string | null | undefined;
  now: Date;
  timeZone: string;
}) => {
  if (lastRunAt) {
    const startedAt = new Date(lastRunAt);
    const elapsedHours = differenceInHours(now, startedAt);

    if (elapsedHours >= 0 && elapsedHours < LIST_LAST_RUN_RELATIVE_HOURS) {
      const relative = formatUpdatedAt(startedAt, { addSuffix: true });

      if (relative !== 'just now') {
        return relative;
      }
    }

    const stamp = formatAutomationRunTimestamp({ at: lastRunAt, now, timeZone });

    return appendTimeZoneAbbrev(stamp, cronSchedule, now, timeZone);
  }

  const nextZoned = getAutomationNextRunZoned({ cronSchedule, now, timeZone });

  if (!nextZoned) {
    return '—';
  }

  const displayTimeZone = getAutomationCronDisplayTimeZone(cronSchedule, timeZone);
  const atIso = zonedPartsToIso(nextZoned, displayTimeZone);
  const stamp = formatAutomationRunTimestamp({ at: atIso, now, timeZone: displayTimeZone });

  return appendTimeZoneAbbrev(stamp, cronSchedule, now, timeZone);
};

export const formatAutomationListCreatedDate = ({
  createdAt,
  now,
  timeZone,
}: {
  createdAt: string;
  now: Date;
  timeZone: string;
}) => {
  const created = new TZDate(createdAt, timeZone);
  const today = new TZDate(now, timeZone);

  if (
    created.getFullYear() === today.getFullYear() &&
    created.getMonth() === today.getMonth() &&
    created.getDate() === today.getDate()
  ) {
    return 'Today';
  }

  const month = MONTH_SHORT[created.getMonth()];
  const day = created.getDate();
  const yearSuffix = created.getFullYear() === today.getFullYear() ? '' : `, ${created.getFullYear()}`;

  return `${month} ${day}${yearSuffix}`;
};
